import React, { useState, useMemo, useEffect } from "react";
import { Row, Col, Button, Tag, Dropdown, message } from "antd";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
import {
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  StopOutlined,
  PlusOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import PageLayout from "@/components/UI/Layout/PageLayout";
import MetricsGrid from "@/components/UI/Layout/MetricGrid";
import StatsAtGlanceCard from "@/components/UI/Cards/standardCard";

import {
  useDeletePromoMutation,
  useUpdatePromoMutation,
} from "@/services/api/promo/promoApi";
import dayjs from "dayjs";
import PromoModal from "./modals/AddPromo";
import RankModal from "./modals/RankModal";
import { usePromoData } from "./hooks/usePromoData";

const Promotions = ({ tableTitle = "All Coupons" }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [modalState, setModalState] = useState({
    open: false,
    mode: "add",
    initialData: null,
  });
  const [openRankModal, setOpenRankModal] = useState(false);

  const {
    promosData,
    paginationInfo,
    summary,
    handlePageChange,
    handlePageSizeChange,
    search,
    handleSearch,
    isLoading,
    isFetching,
  } = usePromoData();

  const [deletePromo] = useDeletePromoMutation();
  const [updatePromo] = useUpdatePromoMutation();

  // Ensure promos is always an array
  const promos = useMemo(() => {
    if (!promosData) return [];
    if (Array.isArray(promosData)) return promosData;
    return [];
  }, [promosData]);

  // Debug logging
  // useEffect(() => {
  //   console.log("Promos data:", promos);
  //   console.log("Active tab:", activeTab);
  // }, [promos, activeTab]);

  const handleTabChange = (index) => {
    // console.log("Tab changed to index:", index);
    setActiveTab(index);
    setSelectedRowKeys([]);
  };

  const openModal = (mode, record = null) => {
    setModalState({
      open: true,
      mode,
      initialData: record,
    });
  };

  const closeModal = () => {
    setModalState({ open: false, mode: "add", initialData: null });
  };

  const handleDeactivate = async (record) => {
    try {
      await updatePromo({
        id: record.id,
        ...record,
        status: false,
      }).unwrap();
      message.success("Promo deactivated successfully");
    } catch (err) {
      message.error(err?.data?.message || "Failed to deactivate promo");
    }
  };

  const handleActivate = async (record) => {
    try {
      await updatePromo({
        id: record.id,
        ...record,
        status: true,
      }).unwrap();
      message.success("Promo activated successfully");
    } catch (err) {
      message.error(err?.data?.message || "Failed to activate promo");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePromo(id).unwrap();
      message.success("Promo deleted successfully");
    } catch (err) {
      message.error(err?.data?.message || "Failed to delete promo");
    }
  };

  // Get status based on dates and status field
  const getPromoStatus = (record) => {
    if (!record) return "Inactive";

    // If status field is explicitly false, it's inactive
    if (record.status === false) return "Inactive";

    // If no end date, it's active (assuming status is true or not explicitly false)
    if (!record.end_date) return "Active";

    // Check if expired based on end date
    const end = dayjs(record.end_date);
    const now = dayjs();

    if (end.isBefore(now, "day")) return "Expired";

    return "Active";
  };

  // Filter data by tab - simplified since we use server-side pagination
  const filteredData = useMemo(() => {
    // console.log("Filtering promos, count:", promos.length);
    return {
      all: promos,
      active: promos.filter((p) => getPromoStatus(p) === "Active"),
      inactive: promos.filter((p) => getPromoStatus(p) === "Inactive"),
      expired: promos.filter((p) => getPromoStatus(p) === "Expired"),
    };
  }, [promos]);

  const getStatusColor = (status) => {
    const map = {
      Active: { color: "#10B981", bg: "#D1FAE5" },
      Inactive: { color: "#F59E0B", bg: "#FEF3C7" },
      Expired: { color: "#EF4444", bg: "#FEE2E2" },
    };
    return map[status] || { color: "#6B7280", bg: "#F3F4F6" };
  };

  const getActionMenuItems = (record) => {
    const status = getPromoStatus(record);
    const items = [
      {
        key: "view",
        icon: <EyeOutlined />,
        label: "View",
        onClick: () => openModal("view", record),
      },
      {
        key: "edit",
        icon: <EditOutlined />,
        label: "Edit",
        onClick: () => openModal("edit", record),
      },
      {
        key: "delete",
        icon: <DeleteOutlined />,
        label: "Delete",
        onClick: () => handleDelete(record),
      },
    ];

    if (status === "Active" || status === "Expired") {
      items.push({
        key: "deactivate",
        icon: <StopOutlined />,
        label: "Deactivate",
        onClick: () => handleDeactivate(record),
      });
    }

    if (status === "Inactive") {
      items.push({
        key: "activate",
        icon: <CheckCircleOutlined />,
        label: "Activate",
        onClick: () => handleActivate(record),
      });
    }

    return items;
  };

  const columns = [
    {
      title: "Promo Name",
      dataIndex: "name",
      key: "name",
      width: 160,
      render: (text) => (
        <span className="font-medium text-gray-800">{text || "—"}</span>
      ),
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 100,
      render: (text) => <Tag color="blue">{text || "—"}</Tag>,
    },
    {
      title: "Type",
      dataIndex: "promotype",
      key: "promotype",
      width: 140,
      render: (type) => <span className="text-sm">{type?.name || "—"}</span>,
    },
    {
      title: "Discount",
      key: "discount",
      width: 120,
      render: (_, record) => {
        if (!record.discount_value) return "—";
        return record.discount_type === "percentage"
          ? `${record.discount_value}%`
          : `₦${record.discount_value.toLocaleString()}`;
      },
    },
    {
      title: "Duration",
      key: "duration",
      width: 180,
      render: (_, record) => {
        if (!record.start_date || !record.end_date)
          return <Tag color="default">No limit</Tag>;

        return (
          <div className="text-xs">
            <div>{dayjs(record.start_date).format("MMM DD, YYYY")}</div>
            <div>→ {dayjs(record.end_date).format("MMM DD, YYYY")}</div>
          </div>
        );
      },
    },
    {
      title: "Usage",
      key: "usage",
      width: 100,
      render: (_, record) => {
        if (!record.usage_limit) return "Unlimited";
        return `${record.usage_count || 0} / ${record.usage_limit}`;
      },
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 140,
      render: (location, record) => {
        if (record.is_global) return <Tag color="purple">Global</Tag>;
        if (!location) return <Tag color="default">All Locations</Tag>;
        return (
          <Tag color="orange">
            {location.name || `Location ${record.location_id}`}
          </Tag>
        );
      },
    },
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 120,
      render: (rank) => {
        if (!rank) return "—";
        return <Tag color="cyan">{rank.name}</Tag>;
      },
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) => {
        const status = getPromoStatus(record);
        const { color, bg } = getStatusColor(status);
        return (
          <Tag
            style={{
              color,
              backgroundColor: bg,
              border: `1px solid ${color}20`,
              borderRadius: "8px",
              padding: "2px 8px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            className="text-gray-400 hover:text-gray-600"
          />
        </Dropdown>
      ),
    },
  ];

  const promoTabs = useMemo(
    () => [
      { label: "All", key: "all", count: paginationInfo.total },
      { label: "Active", key: "active", count: filteredData.active.length },
      {
        label: "Inactive",
        key: "inactive",
        count: filteredData.inactive.length,
      },
      { label: "Expired", key: "expired", count: filteredData.expired.length },
    ],
    [filteredData, paginationInfo]
  );

  // Map index to tab keys
  const tabKeys = ["all", "active", "inactive", "expired"];
  const currentTabKey = tabKeys[activeTab] || "all";

  const actionButtons = [
    {
      label: "Rank",
      type: "default",
      className: "border border-[#f9A022] text-[#D06D01] hover:bg-[#FFF4E6]",
      onClick: () => setOpenRankModal(true),
    },
    {
      label: "Add Promo",
      type: "primary",
      icon: <PlusOutlined />,
      className:
        "bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:bg-gradient-to-l hover:from-[#F9A022] hover:to-[#D06D01] border-0",
      onClick: () => openModal("add"),
    },
  ];
  // console.log("Look here", summary);

  const metrics = useMemo(() => {
    return {
      totalPromos: summary?.total_promos || 0,
      activePromos: summary?.active_promos || 0,
      totalUsed: summary?.total_used || 0,
      expiredPromos: summary?.total_unused || 0,
    };
  }, [summary]);

  const metricData = useMemo(() => {
    return [
      {
        title: "Total Promos",
        value: metrics.totalPromos,
        icon: <FileTextOutlined style={{ fontSize: "24px", color: "#fff" }} />,
        bgColor: "bg-[#E77800]",
        iconBg: "bg-[#F79223]",
        titleColor: "text-[#F3F4F6]",
        valueColor: "text-white",
      },
      {
        title: "Active Promos",
        value: metrics.activePromos,
        icon: (
          <ClockCircleOutlined style={{ fontSize: "24px", color: "#10B981" }} />
        ),
      },
      {
        title: "Total Used",
        value: metrics.totalUsed,
        icon: (
          <CheckCircleOutlined style={{ fontSize: "24px", color: "#F59E0B" }} />
        ),
      },
      {
        title: "Expired",
        value: metrics.expiredPromos,
        icon: <StopOutlined style={{ fontSize: "24px", color: "#EF4444" }} />,
      },
    ];
  }, [metrics]);

  // Current tab data
  const currentTabData = useMemo(() => {
    const data = filteredData[currentTabKey] || [];
    // console.log(
    //   `Data for tab index ${activeTab} (${currentTabKey}):`,
    //   data.length
    // );
    return data;
  }, [filteredData, activeTab, currentTabKey]);

  return (
    <PageLayout>
      <MetricsGrid
        colSpan={{ xs: 24, sm: 12, lg: 6 }}
        CardComponent={StatsAtGlanceCard}
        data={metricData}
      />

      <Row>
        <Col span={24}>
          <CustomDataTable
            title={tableTitle}
            data={promos}
            columns={columns}
            actionButtons={actionButtons}
            tabs={promoTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            loading={isLoading || isFetching}
            showSearch={true}
            searchPlaceholder="Search promos..."
            searchValue={search}
            onServerSearch={handleSearch}
            searchMode="client"
            showFilter={false}
            showCSV={true}
            paginationMode="server"
            currentPage={paginationInfo.current_page}
            totalItems={paginationInfo.total}
            serverPageSize={paginationInfo.per_page}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            selectedRowKeys={selectedRowKeys}
          />
        </Col>
      </Row>

      {/* Reusable Promo Modal */}
      <PromoModal
        open={modalState.open}
        onClose={closeModal}
        mode={modalState.mode}
        initialData={modalState.initialData}
      />

      {/* Rank Modal */}
      <RankModal open={openRankModal} onClose={() => setOpenRankModal(false)} />
    </PageLayout>
  );
};

export default Promotions;
